/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'

import {
    Box,
    Heading,
    Stack,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel
} from '@salesforce/retail-react-app/app/components/shared/ui'
import LinkRefinements from '@salesforce/retail-react-app/app/pages/product-list/partials/link-refinements'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import ColorRefinements from '@salesforce/retail-react-app/app/pages/product-list/partials/color-refinements'
import SizeRefinements from './size-refinements'
import CheckboxRefinements from '../../../../app/pages/product-list/partials/checkbox-refinements'
import {MinusIcon, PlusIcon} from '../../../components/icons'
import {FILTER_ACCORDION_SATE} from '@salesforce/retail-react-app/app/constants'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'

/** Map of refinement attribute IDs to the components used to display values as filter options. */
const componentMap = {
    cgid: LinkRefinements,
    c_refinementColor: ColorRefinements,
    c_size: SizeRefinements,
    price: CheckboxRefinements
}

const Refinements = ({
    itemsBefore,
    excludedFilters = [],
    filters = [],
    toggleFilter,
    selectedFilters,
    isLoading,
    productSearchResultPrms
}) => {
    // Exclude filters in the exclude list.
    filters = filters.filter(({attributeId}) => !excludedFilters.includes(attributeId))

    const segment = useSegment()
    const location = useLocation()
    const categoryId = location.pathname
    const [expandedIndexes, setExpandedIndexes] = useState([])

    // Reset the expanded state on category change
    useEffect(() => {
        // Clear saved accordion state on category change
        if (!isServer && typeof window.localStorage !== 'undefined') {
            window.localStorage.removeItem(FILTER_ACCORDION_SATE)
        }

        // Collapse all when categoryId changes
        setExpandedIndexes([])
    }, [categoryId])

    // Use saved state for accordions
    useEffect(() => {
        if (!isServer && typeof window.localStorage !== 'undefined') {
            // TODO: Change this to `useLocalStorage` hook when localStorage detection is more robust
            const filterAccordionState = window.localStorage.getItem(FILTER_ACCORDION_SATE)
            const savedExpandedAccordionIndexes =
                filterAccordionState && JSON.parse(filterAccordionState)

            if (savedExpandedAccordionIndexes) {
                const indexes = filters
                    ?.map((filter, index) => {
                        if (savedExpandedAccordionIndexes.includes(filter.attributeId)) {
                            return index
                        }
                    })
                    .filter((index) => index !== undefined)

                setExpandedIndexes(indexes)
            }
        }
    }, [filters])

    // Handle saving accordion state
    const updateAccordionState = (expandedIndex) => {
        setExpandedIndexes(expandedIndex)

        const filterState = filters
            ?.filter((_, idx) => expandedIndex.includes(idx))
            .map((filter) => filter.attributeId)

        // TODO: Update when localStorage detection is more robust? useLocalStorage is only a getter
        if (!isServer && typeof window.localStorage !== 'undefined') {
            window.localStorage.setItem(FILTER_ACCORDION_SATE, JSON.stringify(filterState))
        }
    }

    // Handle toggle filter based on context (mobile modal or desktop)
    const handleToggleFilter = (value, attributeId, isChecked) => {
        // Always call the original toggleFilter
        toggleFilter(value, attributeId, isChecked)
        // No need to add any code here that would close a modal
        // The modal closing will be handled separately in the modal component

        if (!isChecked) {
            // Trigger the segment event
            if (typeof segment !== 'undefined' && segment.sendFiltersApplied) {
                segment.sendFiltersApplied(productSearchResultPrms, attributeId, value, filters)
            }
        }
    }

    const scrollbarStyles = {
        '::-webkit-scrollbar': {
            width: '6px',
            height: '6px'
        },
        '::-webkit-scrollbar-thumb': {
            background: '#dddddd',
            borderRadius: '6px',
            height: '6px'
        },
        '::-webkit-scrollbar-thumb:hover': {
            background: '#dddddd',
            height: '6px'
        }
    }

    return (
        <Stack spacing={8}>
            {/* Wait to have filters before rendering the Accordion to allow the default indexes to be accurate */}
            {expandedIndexes && (
                <Accordion
                    index={expandedIndexes}
                    onChange={updateAccordionState}
                    allowMultiple={true}
                    pointerEvents={isLoading ? 'none' : 'auto'}
                    opacity={isLoading ? 0.2 : 1}
                    reduceMotion={true}
                >
                    {itemsBefore}

                    {filters?.map((filter) => {
                        // Render the appropriate component for the refinement type, fallback to checkboxes
                        const Values = componentMap[filter.attributeId] || CheckboxRefinements
                        let selectedFiltersArray = selectedFilters?.[filter.attributeId] ?? []

                        // Catch any non-array values and wrap them in an array
                        if (!Array.isArray(selectedFiltersArray)) {
                            selectedFiltersArray = [selectedFiltersArray]
                        }

                        if (filter.values) {
                            return (
                                <Stack key={filter.attributeId}>
                                    <AccordionItem
                                        py={3}
                                        borderBottom="1px solid"
                                        borderTop="0"
                                        borderColor="#DADADA"
                                    >
                                        {({isExpanded}) => (
                                            <>
                                                <AccordionButton p={0}>
                                                    <Heading
                                                        as="h2"
                                                        flex="1"
                                                        m={0}
                                                        lineHeight="40px"
                                                        color="gray.900"
                                                        fontWeight="extrabold"
                                                        textAlign="left"
                                                        fontSize={'md'}
                                                    >
                                                        {filter.label}
                                                    </Heading>
                                                    <Box
                                                        display="inline-flex"
                                                        alignItems="center"
                                                        justifyContent="start"
                                                        width={'25px'}
                                                    >
                                                        {isExpanded ? (
                                                            <MinusIcon
                                                                width={'16px'}
                                                                height={'16px'}
                                                            />
                                                        ) : (
                                                            <PlusIcon
                                                                width={'16px'}
                                                                height={'16px'}
                                                            />
                                                        )}
                                                    </Box>
                                                </AccordionButton>
                                                <AccordionPanel
                                                    sx={{...scrollbarStyles}}
                                                    paddingLeft={0}
                                                    py={2}
                                                    pt={3}
                                                    overflowY="scroll"
                                                    scrollBehavior="inside"
                                                    position="sticky"
                                                    maxH="300px"
                                                >
                                                    <Values
                                                        selectedFilters={selectedFiltersArray}
                                                        filter={filter}
                                                        toggleFilter={handleToggleFilter}
                                                    />
                                                </AccordionPanel>
                                            </>
                                        )}
                                    </AccordionItem>
                                </Stack>
                            )
                        } else {
                            return null
                        }
                    })}
                </Accordion>
            )}
        </Stack>
    )
}

Refinements.propTypes = {
    itemsBefore: PropTypes.arrayOf(PropTypes.element),
    filters: PropTypes.array,
    excludedFilters: PropTypes.arrayOf(PropTypes.string),
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.object,
    isLoading: PropTypes.bool,
    productSearchResultPrms: PropTypes.object,
    isInMobileModal: PropTypes.bool // Add this to PropTypes
}

export default Refinements
